/**
 * Notification Context
 * Provides notification state and actions throughout the application
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import NotificationService from '../services/NotificationService';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    // Return default values instead of throwing error
    // This allows components to work even when provider is not available
    return {
      notifications: [],
      unreadCount: 0,
      loading: false,
      error: null,
      fetchNotifications: async () => {},
      fetchUnreadNotifications: async () => [],
      markAsRead: async () => {},
      markAllAsRead: async () => {},
      refreshNotifications: async () => {}
    };
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Check if user is authenticated
   */
  const isAuthenticated = useCallback(() => {
    try {
      const authInfo = localStorage.getItem('authInfo');
      return !!authInfo;
    } catch {
      return false;
    }
  }, []);

  /**
   * Fetch all notifications
   */
  const fetchNotifications = useCallback(async () => {
    // Don't fetch if not authenticated
    if (!isAuthenticated()) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await NotificationService.getUserNotifications();
      setNotifications(data || []);
      
      // Calculate unread count
      const unread = (data || []).filter(n => !n.read).length;
      setUnreadCount(unread);
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError(err.message || 'Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  /**
   * Fetch only unread notifications
   */
  const fetchUnreadNotifications = useCallback(async () => {
    // Don't fetch if not authenticated
    if (!isAuthenticated()) {
      return [];
    }

    try {
      const data = await NotificationService.getUnreadNotifications();
      setUnreadCount((data || []).length);
      return data;
    } catch (err) {
      console.error('Error fetching unread notifications:', err);
      return [];
    }
  }, [isAuthenticated]);

  /**
   * Mark a notification as read
   */
  const markAsRead = useCallback(async (notificationId) => {
    try {
      await NotificationService.markAsRead(notificationId);
      
      // Update local state
      setNotifications(prev => 
        prev.map(n => 
          n.id === notificationId 
            ? { ...n, read: true, readAt: new Date().toISOString() }
            : n
        )
      );
      
      // Decrease unread count
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error marking notification as read:', err);
      throw err;
    }
  }, []);

  /**
   * Mark all notifications as read
   */
  const markAllAsRead = useCallback(async () => {
    try {
      await NotificationService.markAllAsRead();
      
      // Update local state
      const now = new Date().toISOString();
      setNotifications(prev => 
        prev.map(n => ({ ...n, read: true, readAt: now }))
      );
      
      // Reset unread count
      setUnreadCount(0);
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
      throw err;
    }
  }, []);

  /**
   * Refresh notifications (polling)
   */
  const refreshNotifications = useCallback(async () => {
    await fetchUnreadNotifications();
  }, [fetchUnreadNotifications]);

  // Initial fetch on mount
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Optional: Set up polling for new notifications every 5 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      refreshNotifications();
    }, 300000); // 5 minutes

    return () => clearInterval(interval);
  }, [refreshNotifications]);

  const value = {
    notifications,
    unreadCount,
    loading,
    error,
    fetchNotifications,
    fetchUnreadNotifications,
    markAsRead,
    markAllAsRead,
    refreshNotifications
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
